import { createContext, useCallback, useContext, useEffect } from 'react';
import { useLocalStorage } from '@/shared/hooks';
import { THEME_CONFIG } from '@/shared/utils/constants';

type Theme = 'dark' | 'light';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const themeMetaColors: Record<Theme, string> = {
  dark: '#020711',
  light: '#f7fbff'
};

const initialState: ThemeProviderState = {
  theme: 'dark',
  setTheme: () => null
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

const isTheme = (value: unknown): value is Theme =>
  value === 'light' || value === 'dark';

export function ThemeProvider({
  children,
  defaultTheme = THEME_CONFIG.DEFAULT_THEME,
  storageKey = THEME_CONFIG.STORAGE_KEY,
  ...props
}: ThemeProviderProps) {
  const [storedTheme, setStoredTheme] = useLocalStorage<Theme | string>(
    storageKey,
    defaultTheme
  );

  const theme = isTheme(storedTheme) ? storedTheme : defaultTheme;

  const setTheme = useCallback(
    (nextTheme: Theme) => setStoredTheme(nextTheme),
    [setStoredTheme]
  );

  useEffect(() => {
    const root = window.document.documentElement;
    const nextTheme = isTheme(storedTheme) ? storedTheme : defaultTheme;

    root.classList.remove('light', 'dark');

    if (storedTheme !== nextTheme) {
      setStoredTheme(nextTheme);
    }

    root.classList.add(nextTheme);
    document
      .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
      ?.setAttribute('content', themeMetaColors[nextTheme]);
  }, [defaultTheme, setStoredTheme, storedTheme]);

  const value = {
    theme,
    setTheme
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
